import _defineProperty from "/home/runner/work/programming-24/programming-24/node_modules/@babel/runtime/helpers/esm/defineProperty.js";
import _slicedToArray from "/home/runner/work/programming-24/programming-24/node_modules/@babel/runtime/helpers/esm/slicedToArray.js";
function ownKeys(object, enumerableOnly) { var keys = Object.keys(object); if (Object.getOwnPropertySymbols) { var symbols = Object.getOwnPropertySymbols(object); enumerableOnly && (symbols = symbols.filter(function (sym) { return Object.getOwnPropertyDescriptor(object, sym).enumerable; })), keys.push.apply(keys, symbols); } return keys; }
function _objectSpread(target) { for (var i = 1; i < arguments.length; i++) { var source = null != arguments[i] ? arguments[i] : {}; i % 2 ? ownKeys(Object(source), !0).forEach(function (key) { _defineProperty(target, key, source[key]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(target, Object.getOwnPropertyDescriptors(source)) : ownKeys(Object(source)).forEach(function (key) { Object.defineProperty(target, key, Object.getOwnPropertyDescriptor(source, key)); }); } return target; }
import loader from '@monaco-editor/loader';
import React, { useState, useRef, useCallback, useEffect } from 'react';
import MonacoContainer from '../MonacoContainer/index.js';
import useMount from '../hooks/useMount/index.js';
import useUpdate from '../hooks/useUpdate/index.js';
import { noop } from '../utils/index.js';
function DiffEditor(_ref) {
  var original = _ref.original,
    modified = _ref.modified,
    language = _ref.language,
    originalLanguage = _ref.originalLanguage,
    modifiedLanguage = _ref.modifiedLanguage,
    originalModelPath = _ref.originalModelPath,
    modifiedModelPath = _ref.modifiedModelPath,
    keepCurrentOriginalModel = _ref.keepCurrentOriginalModel,
    keepCurrentModifiedModel = _ref.keepCurrentModifiedModel,
    theme = _ref.theme,
    loading = _ref.loading,
    options = _ref.options,
    height = _ref.height,
    width = _ref.width,
    className = _ref.className,
    wrapperClassName = _ref.wrapperClassName,
    beforeMount = _ref.beforeMount,
    onMount = _ref.onMount;
  var _useState = useState(false),
    _useState2 = _slicedToArray(_useState, 2),
    isEditorReady = _useState2[0],
    setIsEditorReady = _useState2[1];
  var _useState3 = useState(true),
    _useState4 = _slicedToArray(_useState3, 2),
    isMonacoMounting = _useState4[0],
    setIsMonacoMounting = _useState4[1];
  var editorRef = useRef(null);
  var monacoRef = useRef(null);
  var containerRef = useRef(null);
  var onMountRef = useRef(onMount);
  var beforeMountRef = useRef(beforeMount);
  useMount(function () {
    var cancelable = loader.init();
    cancelable.then(function (monaco) {
      return (monacoRef.current = monaco) && setIsMonacoMounting(false);
    }).catch(function (error) {
      return (error === null || error === void 0 ? void 0 : error.type) !== 'cancelation' && console.error('Monaco initialization: error:', error);
    });
    return function () {
      return editorRef.current ? disposeEditor() : cancelable.cancel();
    };
  });
  useUpdate(function () {
    var modifiedEditor = editorRef.current.getModifiedEditor();
    if (modifiedEditor.getOption(monacoRef.current.editor.EditorOption.readOnly)) {
      modifiedEditor.setValue(modified);
    } else {
      if (modified !== modifiedEditor.getValue()) {
        modifiedEditor.executeEdits('', [{
          range: modifiedEditor.getModel().getFullModelRange(),
          text: modified,
          forceMoveMarkers: true
        }]);
        modifiedEditor.pushUndoStop();
      }
    }
  }, [modified], isEditorReady);
  useUpdate(function () {
    editorRef.current.getModel().original.setValue(original);
  }, [original], isEditorReady);
  useUpdate(function () {
    var _editorRef$current$ge = editorRef.current.getModel(),
      original = _editorRef$current$ge.original,
      modified = _editorRef$current$ge.modified;
    monacoRef.current.editor.setModelLanguage(original, originalLanguage || language);
    monacoRef.current.editor.setModelLanguage(modified, modifiedLanguage || language);
  }, [language, originalLanguage, modifiedLanguage], isEditorReady);
  useUpdate(function () {
    monacoRef.current.editor.setTheme(theme);
  }, [theme], isEditorReady);
  useUpdate(function () {
    editorRef.current.updateOptions(options);
  }, [options], isEditorReady);
  var setModels = useCallback(function () {
    beforeMountRef.current(monacoRef.current);
    var originalModel = monacoRef.current.editor.createModel(original, originalLanguage || language, originalModelPath && monacoRef.current.Uri.parse(originalModelPath));
    var modifiedModel = monacoRef.current.editor.createModel(modified, modifiedLanguage || language, modifiedModelPath && monacoRef.current.Uri.parse(modifiedModelPath));
    editorRef.current.setModel({
      original: originalModel,
      modified: modifiedModel
    });
  }, [language, modified, modifiedLanguage, original, originalLanguage, originalModelPath, modifiedModelPath]);
  var createEditor = useCallback(function () {
    editorRef.current = monacoRef.current.editor.createDiffEditor(containerRef.current, _objectSpread({
      automaticLayout: true
    }, options));
    setModels();
    monacoRef.current.editor.setTheme(theme);
    setIsEditorReady(true);
  }, [options, theme, setModels]);
  useEffect(function () {
    if (isEditorReady) {
      onMountRef.current(editorRef.current, monacoRef.current);
    }
  }, [isEditorReady]);
  useEffect(function () {
    !isMonacoMounting && !isEditorReady && createEditor();
  }, [isMonacoMounting, isEditorReady, createEditor]);
  function disposeEditor() {
    var models = editorRef.current.getModel();
    if (!keepCurrentOriginalModel) {
      var _models$original;
      (_models$original = models.original) === null || _models$original === void 0 ? void 0 : _models$original.dispose();
    }
    if (!keepCurrentModifiedModel) {
      var _models$modified;
      (_models$modified = models.modified) === null || _models$modified === void 0 ? void 0 : _models$modified.dispose();
    }
    editorRef.current.dispose();
  }
  return /*#__PURE__*/React.createElement(MonacoContainer, {
    width: width,
    height: height,
    isEditorReady: isEditorReady,
    loading: loading,
    _ref: containerRef,
    className: className,
    wrapperClassName: wrapperClassName
  });
}
DiffEditor.defaultProps = {
  theme: 'light',
  loading: 'Loading...',
  options: {},
  keepCurrentOriginalModel: false,
  keepCurrentModifiedModel: false,
  /* === */
  width: '100%',
  height: '100%',
  /* === */
  beforeMount: noop,
  onMount: noop
};
export default DiffEditor;